import { auth } from '../firebase';
import logger from './logger';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import axios from 'axios';
import { COLLECTIONS, API_URL } from '../constants/constants';
import { staffClientDebug, staffClientDebugUid } from './staffClientDebug';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { hasMhopClaim, normalizeRoleToMhop } from './mhopCompat';

/**
 * Auth custom claims may expose `locations` as an array or as an object with numeric keys
 * (Firebase rules use the latter shape). Normalize to a string[] for `.includes(site)`.
 * @param {unknown} locations
 * @returns {string[]}
 */
export function normalizeLocationsClaims(locations) {
  return canonicalizePortalLocations(readLocationClaims(locations));
}

function canonicalizePortalLocations(locations) {
  const seen = new Set();
  const next = [];
  locations.forEach((location) => {
    const name = location === 'AWP WRC' ? 'WRC' : location;
    if (!name || seen.has(name)) {
      return;
    }
    seen.add(name);
    next.push(name);
  });
  return next;
}

function readLocationClaims(locations) {
  if (locations === null || locations === undefined) {
    return [];
  }
  if (Array.isArray(locations)) {
    return locations.map(l => String(l).trim()).filter(Boolean);
  }
  if (typeof locations === 'object') {
    const numericKeys = Object.keys(locations)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b));
    if (numericKeys.length > 0) {
      return numericKeys
        .map((k) => locations[k])
        .map((l) => String(l).trim())
        .filter(Boolean);
    }
    return Object.keys(locations)
      .filter((key) => {
        const value = locations[key];
        return value === true || (typeof value === 'string' && value.trim() !== '');
      })
      .map((key) => (typeof locations[key] === 'string' ? String(locations[key]).trim() : String(key).trim()))
      .filter(Boolean);
  }
  return [];
}

/** WRC and AWP WRC refer to the same site in different parts of the portal. */
export function locationClaimsMatch(claimed, requested) {
  if (claimed === null || claimed === undefined || requested === null || requested === undefined) {
    return claimed === requested;
  }
  if (claimed === requested) {
    return true;
  }
  const pair = new Set([claimed, requested]);
  if (pair.has('WRC') && pair.has('AWP WRC')) {
    return true;
  }
  return pair.has('MHOP') && pair.has('Behavioral Health');
}

export function userHasLocationAccess(userLocations, requestedLocation) {
  if (!Array.isArray(userLocations) || !requestedLocation) {
    return false;
  }
  return userLocations.some((loc) => locationClaimsMatch(loc, requestedLocation));
}

/** Merge locations from JWT claims and Firestore user profile (both shapes supported). */
export function mergeUserLocations(...sources) {
  const merged = new Set();
  for (const source of sources) {
    for (const loc of normalizeLocationsClaims(source)) {
      merged.add(loc);
    }
  }
  return [...merged];
}

export const isGoogleAdmin = async (uid) => {
  if (!uid) {
    return false;
  }
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return false;
  }

  if (currentUser.uid === uid) {
    try {
      const token = await currentUser.getIdTokenResult(true);
      return !!token.claims?.admin;
    } catch (error) {
      logger.error('Firebase admin check failed:', error);
    }
  }

  try {
    const response = await axios.get(`${API_URL}/checkAdmin/${uid}`, {
      headers: {
        Authorization: `Bearer ${await currentUser.getIdToken()}`
      }
    });
    return response.data.isAdmin;
  } catch (error) {
    logger.error('Backend admin check failed:', error);
    return false;
  }
};

export const isStaffUser = async (uid) => {
  if (!uid) {
    return false;
  }
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return false;
  }

  try {
    const token = await currentUser.getIdTokenResult(true);
    if (token.claims?.staff) {
      return true;
    }
    if (currentUser.uid === uid) {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid));
      return userDoc.exists() && userDoc.data().role === 'staff';
    }
    if (!token.claims?.admin) {
      logger.error('Non-admin user trying to check staff status of others');
      return false;
    }
    const staffDoc = await getDoc(doc(db, COLLECTIONS.STAFFUSERS, uid));
    return staffDoc.exists();
  } catch (error) {
    logger.error('Error checking staff status:', error);
    return false;
  }
};

export const isManagerUser = async (uid = null) => {
  try {
    const targetUid = uid || auth.currentUser?.uid;
    if (!targetUid) {
      return false;
    }

    const currentUser = auth.currentUser;
    if (currentUser?.uid === targetUid) {
      const token = await currentUser.getIdTokenResult(true);
      if (token.claims?.manager || token.claims?.director || token.claims?.admin) {
        return true;
      }
    }

    const isAdmin = await isGoogleAdmin(currentUser?.uid);
    if (!isAdmin) {
      return false;
    }

    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, targetUid));
    const r = userDoc.data()?.role;
    return userDoc.exists() && (r === 'manager' || r === 'director' || r === 'admin');
  } catch (error) {
    logger.error('Error checking manager status:', error);
    return false;
  }
};

export const isMhopUser = (user) => {
  if (!user) {
    return false;
  }

  if (hasMhopClaim(user.claims || {})) {
    return true;
  }

  const userData = user.data?.();
  if (hasMhopClaim(userData?.customClaims || {})) {
    return true;
  }
  const role = normalizeRoleToMhop(userData?.role);
  if (role === 'mhop') {
    return true;
  }

  return false;
};

export const isBehavioralHealthUser = isMhopUser;

/** MHOP or admin — access to MHOP program page and caseload tracker */
export const canAccessMhopProgram = (user, claims = null) => {
  if (!user) {
    return false;
  }
  const c = claims || user.claims || user.customClaims || {};
  const isAdmin = c.admin === true || user.isAdmin === true || user.role === 'admin';
  const isMhop =
    hasMhopClaim(c) ||
    user.isMhop === true ||
    user.isBehavioralHealth === true ||
    normalizeRoleToMhop(user.role) === 'mhop';
  return isAdmin || isMhop;
};

export const canAccessBehavioralHealthProgram = canAccessMhopProgram;

/** True when Auth custom claims grant portal admin (not Firestore role alone). */
export const hasAdminAuthClaim = (user, claims = null) => {
  if (!user) {
    return false;
  }
  const c = claims || user.claims || user.customClaims || {};
  return c.admin === true || c.role === 'admin';
};

/** Firestore profile says admin, but the Auth JWT claim is not set. */
export const needsAdminClaimRefresh = (user, claims = null) =>
  Boolean(user?.role === 'admin' && !hasAdminAuthClaim(user, claims));

/** App admin — JWT custom claim only, matching NavBar / AuthContext */
export const isAppAdmin = (user, claims = null) => {
  if (!user) {
    return false;
  }
  return hasAdminAuthClaim(user, claims) || user.isAdmin === true;
};

const CLIENT_LIST_FIRESTORE_ROLES = new Set([
  'admin',
  'manager',
  'director',
  'programManager',
  'staff',
  'mhop',
  'behavioralHealth',
  'behavioral_health',
  'clinician',
  'caseManagerStaff',
  'gss'
]);

const STAFF_LIKE_FIRESTORE_ROLES = [
  'staff',
  'mhop',
  'behavioralHealth',
  'behavioral_health',
  'clinician',
  'caseManagerStaff',
  'gss'
];

export const claimsGrantNamedClientRole = (claims = {}) =>
  claims.admin === true ||
  claims.manager === true ||
  claims.director === true ||
  claims.programManager === true ||
  hasMhopClaim(claims) ||
  claims.staff === true ||
  claims.gss === true ||
  claims.caseManagerStaff === true;

export const claimsGrantClientList = (claims = {}) =>
  claimsGrantNamedClientRole(claims) ||
  normalizeLocationsClaims(claims.locations).length > 0;

/** Development testers cannot read client records unless they also have a staff-like role. */
export const developmentBlocksClientRecords = (claims = {}) =>
  claims.development === true && !claimsGrantNamedClientRole(claims);

export const claimsAllowClientRecords = (claims = {}) =>
  !developmentBlocksClientRecords(claims) && claimsGrantClientList(claims);

const CLIENT_LIST_AUTH_CACHE_MS = 4000;
const clientListAuthCache = new Map();

/**
 * Resolve who may list clients — aligns JWT claims with Firestore user doc (UI uses both).
 * @param {import('firebase/auth').User} user
 * @returns {Promise<{
 *   isAdmin: boolean,
 *   hasFullClientListAccess: boolean,
 *   userLocations: string[],
 *   claims: Record<string, unknown>,
 *   firestoreRole: string,
 *   shouldUseApiFallback: boolean,
 *   shouldUseApiFallbackForList: boolean,
 *   shouldUseApiFallbackForProfile: boolean
 * }>}
 */
export const resolveClientListAuth = async (user) => {
  if (!user?.uid) {
    throw new Error('User not authenticated');
  }

  const cached = clientListAuthCache.get(user.uid);
  if (cached && Date.now() - cached.at < CLIENT_LIST_AUTH_CACHE_MS) {
    return cached.value;
  }

  let tokenResult = await user.getIdTokenResult(false);
  let claims = tokenResult.claims || {};

  const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
  const userData = userDoc.exists() ? userDoc.data() : {};
  const firestoreRole = userData.role || '';

  const firestoreGrantsList = CLIENT_LIST_FIRESTORE_ROLES.has(firestoreRole);
  if (firestoreGrantsList && !claimsGrantClientList(claims)) {
    tokenResult = await user.getIdTokenResult(true);
    claims = tokenResult.claims || {};
  }

  const isAdmin =
    claims.admin === true ||
    firestoreRole === 'admin';
  const hasFullClientListAccess =
    isAdmin ||
    claims.manager === true ||
    claims.director === true ||
    ['manager', 'director'].includes(firestoreRole);
  const hasStaffLikeClientListAccess =
    claims.staff === true ||
    claims.gss === true ||
    claims.programManager === true ||
    hasMhopClaim(claims) ||
    claims.caseManagerStaff === true ||
    STAFF_LIKE_FIRESTORE_ROLES.includes(firestoreRole) ||
    firestoreRole === 'programManager';
  const userLocations = mergeUserLocations(claims.locations, userData.locations);
  const firestoreGlobalProfileRole = ['mhop', 'behavioralHealth', 'behavioral_health', 'clinician', 'caseManagerStaff'].includes(firestoreRole);
  const claimsGrantGlobalProfile =
    hasMhopClaim(claims) ||
    claims.caseManagerStaff === true;
  const shouldUseApiFallbackForList =
    firestoreGrantsList && !claimsGrantClientList(claims);
  const shouldUseApiFallbackForProfile =
    firestoreGlobalProfileRole && !claimsGrantGlobalProfile;
  const shouldUseApiFallback =
    shouldUseApiFallbackForList || shouldUseApiFallbackForProfile;

  const authResult = {
    isAdmin,
    hasFullClientListAccess,
    hasStaffLikeClientListAccess,
    userLocations,
    claims,
    firestoreRole,
    shouldUseApiFallback,
    shouldUseApiFallbackForList,
    shouldUseApiFallbackForProfile
  };

  staffClientDebug('resolveClientListAuth', {
    uid: staffClientDebugUid(user.uid),
    firestoreRole,
    isAdmin,
    hasFullClientListAccess,
    hasStaffLikeClientListAccess,
    shouldUseApiFallback,
    claimsLocations: normalizeLocationsClaims(claims.locations),
    profileLocations: normalizeLocationsClaims(userData.locations),
    mergedLocations: userLocations,
    staffClaim: claims.staff === true,
    managerClaim: claims.manager === true
  });

  clientListAuthCache.set(user.uid, { at: Date.now(), value: authResult });
  return authResult;
};

function housingSiteTierAccessFromClaims(claims = {}, contextUser = {}) {
  return (
    contextUser.isAdmin === true ||
    claims.admin === true ||
    contextUser.isExecutive === true ||
    claims.executive === true ||
    contextUser.isManager === true ||
    claims.manager === true ||
    contextUser.isDirector === true ||
    claims.director === true
  );
}


/**
 * Leadership roles that bypass site location checks on ProtectedRoute.
 * @param {object|null|undefined} user AuthContext user
 */
export function routeTierBypassFromUser(user) {
  if (!user) {
    return {
      isAdmin: false,
      isManager: false,
      isDirector: false,
      isExecutive: false,
      isCompliance: false,
      isDevelopment: false
    };
  }
  const claims = user.claims || {};
  const isAdmin = user.isAdmin === true || claims.admin === true || claims.role === 'admin';
  const isExecutive = user.isExecutive === true || claims.executive === true;
  const isDirector = user.isDirector === true || claims.director === true;
  const isManager =
    user.isManager === true ||
    claims.manager === true ||
    isDirector;
  const isCompliance = user.isCompliance === true || claims.compliance === true;
  const isDevelopment = user.isDevelopment === true || claims.development === true;
  return { isAdmin, isManager, isDirector, isExecutive, isCompliance, isDevelopment };
}

/**
 * Reporting menu and `/reports` access (matches NavBar `canViewReporting` and the
 * `/api/reporting` role check).
 * @param {object|null|undefined} user AuthContext user
 */
export function canViewReportingFromUser(user) {
  const tier = routeTierBypassFromUser(user);
  return tier.isAdmin || tier.isManager || tier.isExecutive || tier.isCompliance || tier.isDevelopment;
}

/**
 * @param {string|null|undefined} role Firestore users.role
 */
export function routeTierBypassFromFirestoreRole(role) {
  const normalized = String(role || '').trim();
  return {
    isAdmin: normalized === 'admin',
    isExecutive: normalized === 'executive',
    isDirector: normalized === 'director',
    isManager: normalized === 'manager' || normalized === 'director',
    isCompliance: normalized === 'compliance',
    isDevelopment: normalized === 'development'
  };
}

export function userHasRouteTierBypass(user, firestoreRole = null) {
  const fromUser = routeTierBypassFromUser(user);
  const fromProfile = routeTierBypassFromFirestoreRole(firestoreRole);
  return {
    isAdmin: fromUser.isAdmin || fromProfile.isAdmin,
    isManager: fromUser.isManager || fromProfile.isManager,
    isDirector: fromUser.isDirector || fromProfile.isDirector,
    isExecutive: fromUser.isExecutive || fromProfile.isExecutive,
    isCompliance: fromUser.isCompliance || fromProfile.isCompliance,
    isDevelopment: fromUser.isDevelopment || fromProfile.isDevelopment
  };
}

export function routeTierBypassAllowsLocation(user, firestoreRole, allowCompliance = false) {
  const tier = userHasRouteTierBypass(user, firestoreRole);
  return (
    tier.isAdmin ||
    tier.isManager ||
    tier.isExecutive ||
    (allowCompliance && (tier.isCompliance || tier.isDevelopment))
  );
}

/**
 * Sync housing site access from AuthContext user (button enablement, nav).
 * Matches functions/routes/housing.js `userHasHousingSiteAccess`.
 *
 * @param {object} currentUser AuthContext user
 * @param {'Coronado' | 'Eddy'} site
 * @returns {boolean}
 */
export function userHasHousingSiteAccessFromContext(currentUser, site) {
  if (!currentUser || !site) {
    return false;
  }
  const claims = currentUser.claims || {};
  if (housingSiteTierAccessFromClaims(claims, currentUser)) {
    return true;
  }
  const locations = mergeUserLocations(currentUser.locations, claims.locations);
  return userHasLocationAccess(locations, site);
}

/**
 * Merge JWT claim locations with Firestore user profile locations.
 *
 * @param {import('firebase/auth').User} user
 * @returns {Promise<string[]>}
 */
export async function fetchMergedUserLocations(user) {
  const access = await fetchMergedUserRouteAccess(user);
  return access.locations;
}

/**
 * Merge JWT claim locations with Firestore profile locations and role.
 *
 * @param {import('firebase/auth').User} user
 * @returns {Promise<{ locations: string[], firestoreRole: string|null }>}
 */
export async function fetchMergedUserRouteAccess(user) {
  if (!user?.uid) {
    return { locations: [], firestoreRole: null };
  }
  const tokenResult = await user.getIdTokenResult(false);
  const claims = tokenResult.claims || {};
  const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
  const userData = userDoc.exists() ? userDoc.data() : {};
  return {
    locations: mergeUserLocations(claims.locations, userData.locations),
    firestoreRole: userData.role || null
  };
}

/**
 * Async housing site access — merges JWT claims with Firestore user profile.
 *
 * @param {import('firebase/auth').User} user
 * @param {'Coronado' | 'Eddy'} site
 * @returns {Promise<{
 *   hasAccess: boolean,
 *   isAdmin: boolean,
 *   isManager: boolean,
 *   isDirector: boolean,
 *   isExecutive: boolean,
 *   userLocations: string[]
 * }>}
 */
export async function resolveHousingSiteAccess(user, site) {
  const denied = {
    hasAccess: false,
    isAdmin: false,
    isManager: false,
    isDirector: false,
    isExecutive: false,
    userLocations: []
  };

  if (!user?.uid || !site) {
    return denied;
  }

  let tokenResult = await user.getIdTokenResult(false);
  let claims = tokenResult.claims || {};

  const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
  const userData = userDoc.exists() ? userDoc.data() : {};
  const firestoreRole = userData.role || '';

  let userLocations = mergeUserLocations(claims.locations, userData.locations);

  const isAdmin = claims.admin === true || firestoreRole === 'admin';
  const isManager =
    claims.manager === true ||
    ['manager', 'director'].includes(firestoreRole);
  const isDirector = claims.director === true || firestoreRole === 'director';
  const isExecutive = claims.executive === true || firestoreRole === 'executive';
  const isGuest = claims.guest === true || firestoreRole === 'guest';
  const guestPrograms = claims.guestPrograms === true;
  const tierAccess = isAdmin || isExecutive || isManager || isDirector;

  if (isGuest) {
    if (!guestPrograms) {
      return denied;
    }
    const guestLocations = mergeUserLocations(claims.locations, userData.locations);
    return {
      hasAccess: userHasLocationAccess(guestLocations, site),
      isAdmin: false,
      isManager: false,
      isDirector: false,
      isExecutive: false,
      userLocations: guestLocations
    };
  }

  const profileGrantsSite = userHasLocationAccess(userLocations, site);
  const claimsGrantSite = userHasLocationAccess(normalizeLocationsClaims(claims.locations), site);

  if (profileGrantsSite && !claimsGrantSite) {
    tokenResult = await user.getIdTokenResult(true);
    claims = tokenResult.claims || {};
    userLocations = mergeUserLocations(claims.locations, userData.locations);
  }

  const hasAccess = tierAccess || userHasLocationAccess(userLocations, site);

  return {
    hasAccess,
    isAdmin,
    isManager: claims.manager === true || firestoreRole === 'manager',
    isDirector,
    isExecutive,
    userLocations
  };
}

/**
 * Merged role flags + locations from JWT claims and Firestore user profile.
 *
 * @param {import('firebase/auth').User} user
 * @returns {Promise<{
 *   isAdmin: boolean,
 *   isManager: boolean,
 *   isDirector: boolean,
 *   isExecutive: boolean,
 *   isStaff: boolean,
 *   userLocations: string[]
 * }>}
 */
export async function resolveUserLocationPermissions(user) {
  const empty = {
    isAdmin: false,
    isManager: false,
    isDirector: false,
    isExecutive: false,
    isStaff: false,
    userLocations: []
  };

  if (!user?.uid) {
    return empty;
  }

  const tokenResult = await user.getIdTokenResult(false);
  const claims = tokenResult.claims || {};
  const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
  const userData = userDoc.exists() ? userDoc.data() : {};
  const firestoreRole = userData.role || '';

  return {
    isAdmin: claims.admin === true || firestoreRole === 'admin',
    isManager: claims.manager === true || firestoreRole === 'manager',
    isDirector: claims.director === true || firestoreRole === 'director',
    isExecutive: claims.executive === true || firestoreRole === 'executive',
    isStaff:
      claims.staff === true ||
      hasMhopClaim(claims) ||
      claims.caseManagerStaff === true ||
      ['staff', 'mhop', 'behavioralHealth', 'behavioral_health', 'clinician', 'caseManagerStaff'].includes(firestoreRole),
    userLocations: mergeUserLocations(claims.locations, userData.locations, user.locations)
  };
}

/**
 * Program page / daily-log access — merged locations; optional staff bypass (Drop-In).
 *
 * @param {import('firebase/auth').User} user
 * @param {string} site
 * @param {{ allowStaff?: boolean }} [options]
 * @returns {Promise<Awaited<ReturnType<typeof resolveHousingSiteAccess>>>}
 */
export async function resolveProgramSiteAccess(user, site, options = {}) {
  const { allowStaff = false } = options;
  const access = await resolveHousingSiteAccess(user, site);
  if (access.hasAccess || !allowStaff) {
    return access;
  }
  const perms = await resolveUserLocationPermissions(user);
  if (!perms.isStaff) {
    return access;
  }
  return {
    ...access,
    hasAccess: true,
    isAdmin: access.isAdmin || perms.isAdmin,
    isManager: access.isManager || perms.isManager,
    isDirector: access.isDirector || perms.isDirector,
    isExecutive: access.isExecutive || perms.isExecutive,
    userLocations: perms.userLocations.length > 0 ? perms.userLocations : access.userLocations
  };
}

export const getUserRole = async (uid = null) => {
  const currentUser = auth.currentUser;
  const targetUid = uid || currentUser?.uid;

  if (!targetUid) {
    return {
      isAdmin: false,
      isDirector: false,
      isManager: false,
      isMhop: false,
      isBehavioralHealth: false,
      isCaseManagerStaff: false,
      isStaff: false,
      isHR: false,
      isCompliance: false,
      isExecutive: false,
      error: 'no-user'
    };
  }

  try {
    const token = await currentUser.getIdTokenResult(true);
    return {
      isAdmin: token.claims?.admin || false,
      isDirector: token.claims?.director || false,
      isManager: !!(token.claims?.manager || token.claims?.director),
      isMhop: hasMhopClaim(token.claims || {}),
      isBehavioralHealth: hasMhopClaim(token.claims || {}),
      isCaseManagerStaff: token.claims?.caseManagerStaff === true,
      isStaff: token.claims?.staff || false,
      isHR: token.claims?.hr || false,
      isCompliance: token.claims?.compliance ?? false,
      isExecutive: token.claims?.executive ?? false,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    logger.error('Role check failed:', error);
    return {
      isAdmin: false,
      isDirector: false,
      isManager: false,
      isMhop: false,
      isBehavioralHealth: false,
      isCaseManagerStaff: false,
      isStaff: false,
      isHR: false,
      isCompliance: false,
      isExecutive: false,
      error: error.message
    };
  }
};

export const updateUserRole = async (userId, role, action) => {
  try {
    logger.debug('Updating role with:', { userId, role, action });

    // Validate inputs
    if (!userId || typeof userId !== 'string') {
      throw new Error('Invalid userId: must be a non-empty string');
    }
    if (
      !role ||
    !['admin', 'director', 'manager', 'programManager', 'staff', 'mhop', 'behavioralHealth', 'caseManagerStaff', 'hr', 'security', 'compliance', 'executive', 'operations', 'gss', 'relief', 'reliefManager', 'development'].includes(role)) {
      throw new Error(
        'Invalid role: must be one of "admin", "director", "manager", "programManager", "staff", "mhop", "behavioralHealth", "caseManagerStaff", "hr", "security", "compliance", "executive", "operations", "gss", "relief", "reliefManager", or "development"'
      );
    }
    if (!action || !['grant', 'revoke'].includes(action)) {
      throw new Error('Invalid action: must be either "grant" or "revoke"');
    }

    // Check if current user is admin
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to perform this action');
    }

    // Force token refresh to get latest claims
    const tokenResult = await currentUser.getIdTokenResult(true);
    if (!tokenResult.claims?.admin) {
      throw new Error('Admin privileges required');
    }

    // Check if user exists in Firestore
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    if (!userDoc.exists()) {
      throw new Error('User not found in database');
    }

    // Call the Cloud Function
    try {
      const manageRole = httpsCallable(functions, 'manageUserRole');
      logger.debug('Sending role update request:', { userId, role, action });
      logger.debug('Functions object:', functions);
      logger.debug('manageRole function:', manageRole);

      const result = await manageRole({
        userId,
        role,
        action
      });

      logger.debug('Role update response:', result.data);
      return result.data;
    } catch (error) {
      logger.error('Role update failed:', error);
      logger.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        stack: error.stack
      });
      if (error.code === 'functions/invalid-argument') {
        throw new Error(error.message || 'Invalid input data');
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to perform this action');
      } else if (error.code === 'functions/unauthenticated') {
        throw new Error('You must be logged in to perform this action');
      } else {
        throw error;
      }
    }
  } catch (error) {
    logger.error('Error in updateUserRole:', error);
    throw error;
  }
};

export const revokeAdminPrivileges = async (uid) => {
  return updateUserRole(uid, 'admin', 'revoke');
};

export const promoteToAdmin = async (uid) => {
  return updateUserRole(uid, 'admin', 'grant');
};

export const revokeManagerPrivileges = async (uid) => {
  return updateUserRole(uid, 'manager', 'revoke');
};

export const promoteToManager = async (uid) => {
  return updateUserRole(uid, 'manager', 'grant');
};

export const revokeProgramManagerPrivileges = async (uid) => {
  return updateUserRole(uid, 'programManager', 'revoke');
};

export const promoteToProgramManager = async (uid) => {
  return updateUserRole(uid, 'programManager', 'grant');
};

export const revokeDirectorPrivileges = async (uid) => {
  return updateUserRole(uid, 'director', 'revoke');
};

export const promoteToDirector = async (uid) => {
  return updateUserRole(uid, 'director', 'grant');
};

export const revokeCaseManagerStaffPrivileges = async (uid) => {
  return updateUserRole(uid, 'caseManagerStaff', 'revoke');
};

export const promoteToCaseManagerStaff = async (uid) => {
  return updateUserRole(uid, 'caseManagerStaff', 'grant');
};

export const revokeMhopPrivileges = async (uid) => {
  return updateUserRole(uid, 'mhop', 'revoke');
};

export const promoteToMhop = async (uid) => {
  logger.debug('=== promoteToMhop called ===');
  logger.debug('UID:', uid);
  try {
    const result = await updateUserRole(uid, 'mhop', 'grant');
    logger.debug('promoteToMhop result:', result);
    return result;
  } catch (error) {
    logger.error('promoteToMhop error:', error);
    throw error;
  }
};

export const revokeBehavioralHealthPrivileges = revokeMhopPrivileges;
export const promoteToBehavioralHealth = promoteToMhop;

export const revokeStaffPrivileges = async (uid) => {
  return updateUserRole(uid, 'staff', 'revoke');
};

export const promoteToStaff = async (uid) => {
  return updateUserRole(uid, 'staff', 'grant');
};

export const revokeHRPrivileges = async (uid) => {
  return updateUserRole(uid, 'hr', 'revoke');
};

export const promoteToHR = async (uid) => {
  return updateUserRole(uid, 'hr', 'grant');
};

export const isHRUser = async (uid) => {
  try {
    const role = await getUserRole(uid);
    return role.isHR;
  } catch (error) {
    logger.error('Error checking HR status:', error);
    return false;
  }
};

/** User can appear as another employee's manager (admin, director, manager, executive). */
export const isManagerTierUser = (user) => {
  if (!user) {
    return false;
  }
  const c = user.customClaims || {};
  const r = user.role;
  return (
    c.admin === true ||
    c.director === true ||
    c.manager === true ||
    c.executive === true ||
    ['admin', 'director', 'manager', 'executive'].includes(r));

};

export const promoteToSecurity = async (uid) => {
  return updateUserRole(uid, 'security', 'grant');
};

export const revokeSecurityPrivileges = async (uid) => {
  return updateUserRole(uid, 'security', 'revoke');
};

export const promoteToCompliance = async (uid) => {
  return updateUserRole(uid, 'compliance', 'grant');
};

export const revokeCompliancePrivileges = async (uid) => {
  return updateUserRole(uid, 'compliance', 'revoke');
};

export const promoteToExecutive = async (uid) => {
  return updateUserRole(uid, 'executive', 'grant');
};

export const revokeExecutivePrivileges = async (uid) => {
  return updateUserRole(uid, 'executive', 'revoke');
};
export const revokeOperationsPrivileges = async (uid) => {
  return updateUserRole(uid, 'operations', 'revoke');
};

export const promoteToOperations = async (uid) => {
  return updateUserRole(uid, 'operations', 'grant');
};

export const revokeGssPrivileges = async (uid) => {
  return updateUserRole(uid, 'gss', 'revoke');
};

export const promoteToGss = async (uid) => {
  return updateUserRole(uid, 'gss', 'grant');
};

export const revokeReliefPrivileges = async (uid) => {
  return updateUserRole(uid, 'relief', 'revoke');
};

export const promoteToRelief = async (uid) => {
  return updateUserRole(uid, 'relief', 'grant');
};

export const revokeReliefManagerPrivileges = async (uid) => {
  return updateUserRole(uid, 'reliefManager', 'revoke');
};

export const promoteToReliefManager = async (uid) => {
  return updateUserRole(uid, 'reliefManager', 'grant');
};

export const revokeDevelopmentPrivileges = async (uid) => {
  return updateUserRole(uid, 'development', 'revoke');
};

export const promoteToDevelopment = async (uid) => {
  return updateUserRole(uid, 'development', 'grant');
};

